import {
  ArrowRight,
  BatteryCharging,
  Building2,
  CalendarCheck,
  CheckCircle2,
  ClipboardCheck,
  Download,
  FileText,
  Gauge,
  HardHat,
  Landmark,
  PoundSterling,
  ShieldCheck,
  Sun,
  Sprout,
  TreePine,
  Users,
  Wrench,
  Zap,
} from "lucide-react";

import { B2BEnquiryForm } from "@/components/commercial/b2b-enquiry-form";
import { HeroBackgroundPicture } from "@/components/hero-background-picture";
import {
  CommercialAccreditationsTable,
  SOLAR_COMPLIANCE_ROWS,
} from "@/components/commercial/commercial-accreditations-table";
import { CaseStudiesSection } from "@/components/commercial/case-studies-section";
import { CommercialPrimaryCta, CommercialSecondaryCta } from "@/components/commercial/commercial-cta";
import { CommercialSocialValue } from "@/components/commercial/commercial-social-value";
import { COMMERCIAL_URLS } from "@/content/commercial/urls";
import { SECTOR_PAGE_LINKS } from "@/content/commercial/sectors";

import { PageBreadcrumb } from "@/components/page-breadcrumb";
import { commercialBreadcrumbTrail } from "@/lib/breadcrumbs";
import { getRasterImage } from "@/generated/raster-images";
import { AccreditationStrip } from "@/components/site-blocks";

import crsLogo from "@/assets/crs-logo-white.webp";

const PHONE_LABEL = "0800 009 2992";
const PHONE_TEL = "tel:+448000092992";
const SOURCE_PAGE = "/commercial-solar-pv";

const SECTOR_LINKS: Record<string, string> = Object.fromEntries(
  SECTOR_PAGE_LINKS.map((s) => [s.name, s.path])
);

export const COMMERCIAL_SOLAR_FAQS = [
  {
    question: "Are you framework-ready for council and housing-association procurement?",
    answer:
      "Yes. We work with councils, housing associations, facilities managers and main contractors under framework agreements, with RAMS, insurances, accreditations and reporting expected at that level. A current capability statement is available on request.",
  },
  {
    question: "Do you handle portfolio rollouts across multiple sites?",
    answer:
      "Yes. We mobilise portfolio surveys, sequence design and DNO/G99 applications across sites, and run a coordinated programme rather than one-off installs. Programme adherence, H&S and snagging are reported back to the buyer.",
  },
  {
    question: "Can you manage larger DNO / G99 connections?",
    answer:
      "Yes. We prepare and submit DNO / G99 applications, including larger commercial connections, and coordinate with the network operator through the design and install phases.",
  },
  {
    question: "Do you provide social-value reporting?",
    answer:
      "Yes. We report on local jobs, apprenticeships, local supply chain and community benefit on a per-project or per-portfolio basis. Specifics are agreed up front to match the buyer's social-value framework.",
  },
  {
    question: "What finance or PPA options are available?",
    answer:
      "Purchase, finance and PPA routes can be considered for commercial projects. All APR, repayment terms, PPA pricing and ROI evidence are confirmed in writing per project.",
  },
  {
    question: "Is a capability statement available?",
    answer:
      "Yes. We provide a current capability statement and accreditation pack on request, with renewal dates for each accreditation and insurance to reduce diligence friction.",
  },
];

const HERO_TRUST = [
  "MCS Certified",
  "Commercial G99 / DNO",
  "Framework-Ready Delivery",
  "Social Value Reporting",
  "Insurance-Backed",
];

const SECTOR_CARDS = [
  {
    Icon: Landmark,
    title: "Councils",
    body: "Decarbonisation programmes across estates, schools and operational sites. Framework delivery with social-value and KPI reporting.",
    href: SECTOR_LINKS["Councils"],
  },
  {
    Icon: Building2,
    title: "Housing Associations",
    body: "Portfolio rooftop rollout across social-housing stock, with resident comms support and completion reporting.",
    href: SECTOR_LINKS["Housing Associations"],
  },
  {
    Icon: HardHat,
    title: "Facilities Management",
    body: "Solar across managed estates, coordinated with planned maintenance windows and tenant operations.",
    href: SECTOR_LINKS["Facilities Management"],
  },
  {
    Icon: ClipboardCheck,
    title: "Main Contractors",
    body: "Subcontract M&E / renewable scope delivered to programme, with RAMS, ITPs and snagging discipline expected on tier-one sites.",
    href: SECTOR_LINKS["Main Contractors"],
  },
  {
    Icon: Sun,
    title: "Commercial Owners",
    body: "Industrial, warehouse, retail and office rooftop arrays, with DNO/G99, optional battery and EV-readiness.",
  },
  {
    Icon: Sprout,
    title: "Agriculture / SMEs",
    body: "Barn-roof and ground-mount arrays for working farms and rural businesses, including battery and on-site use.",
  },
];

const WHY_CARDS = [
  {
    Icon: PoundSterling,
    title: "Energy Cost Control",
    body: "On-site generation lowers exposure to grid pricing. Real-world numbers are modelled per site, never quoted generically.",
  },
  {
    Icon: TreePine,
    title: "Decarbonisation Targets",
    body: "Solar contributes to estate-wide carbon reduction. Carbon claims are evidenced per project against the buyer's reporting framework.",
  },
  {
    Icon: Users,
    title: "Carbon & Social Value",
    body: "Local jobs, apprenticeships, supply chain and community benefit reported back to the buyer in a format they can use in their own reporting.",
  },
];

const SCOPE_CARDS = [
  {
    Icon: Sun,
    title: "Rooftop Arrays",
    body: "Industrial, warehouse, school, office and social-housing roofs. Structural and condition checks built into the design phase.",
  },
  {
    Icon: Sprout,
    title: "Ground-Mount",
    body: "Ground-mount arrays for sites with available land, including planning coordination where required.",
  },
  {
    Icon: Building2,
    title: "Car-Port / Canopy",
    body: "Solar canopies over car parks delivering generation plus shaded parking, with cable routes designed in.",
  },
  {
    Icon: BatteryCharging,
    title: "Battery Storage",
    body: "Commercial battery storage for self-consumption, peak shaving and resilience. Sized to the site's load profile.",
  },
  {
    Icon: Zap,
    title: "EV-Readiness",
    body: "Infrastructure designed with EV charging capacity in mind, so future chargers don't require a second dig-up.",
  },
  {
    Icon: ClipboardCheck,
    title: "New-Build & Retrofit",
    body: "Coordinated with new-build M&E packages or delivered into occupied retrofit environments.",
  },
];

const MOBILISATION = [
  {
    Icon: FileText,
    title: "Feasibility & Surveys",
    body: "Portfolio or single-site feasibility, structural and electrical surveys, shading and orientation analysis.",
  },
  {
    Icon: ClipboardCheck,
    title: "Design & DNO / G99",
    body: "Property-specific design and DNO/G99 application, including larger commercial connections.",
  },
  {
    Icon: CalendarCheck,
    title: "Programme & RAMS",
    body: "Programme aligned to the buyer's schedule with full RAMS, ITPs, permits and site induction.",
  },
  {
    Icon: HardHat,
    title: "Site Management",
    body: "Dedicated site management, subcontract coordination and daily reporting under framework agreements.",
  },
];

const KPI_CARDS = [
  {
    Icon: CalendarCheck,
    title: "Programme Adherence",
    body: "Weekly programme reporting against the agreed schedule, with early-warning of risk.",
  },
  {
    Icon: ShieldCheck,
    title: "Health & Safety",
    body: "RAMS, ITPs, permits, induction and incident reporting documented and shared.",
  },
  {
    Icon: ClipboardCheck,
    title: "Snagging & Quality",
    body: "Snag list management with closure evidence, photo records and sign-off.",
  },
  {
    Icon: Gauge,
    title: "Generation & Commissioning",
    body: "Commissioning records, MCS certification and monitoring data on completion.",
  },
  {
    Icon: TreePine,
    title: "Carbon Reporting",
    body: "Project-level carbon reporting in the buyer's format. Claims evidenced, not generic.",
  },
  {
    Icon: Users,
    title: "Social Value Reporting",
    body: "Local jobs, apprenticeships, supply chain and community benefit reported per project.",
  },
];

const PROCESS_STEPS = [
  {
    step: 1,
    title: "Feasibility & Design",
    description:
      "Portfolio or single-site feasibility, structural and electrical surveys, property-specific design.",
  },
  {
    step: 2,
    title: "Proposal & Options",
    description: "Written proposal with purchase, finance and PPA options where applicable.",
  },
  {
    step: 3,
    title: "Install & Commission",
    description:
      "Framework-grade install with RAMS, ITPs, daily reporting, commissioning and MCS certification.",
  },
  {
    step: 4,
    title: "Monitoring & Reporting",
    description:
      "Generation monitoring, carbon and social-value reporting back to the buyer in their preferred format.",
  },
];

const B2B_TESTIMONIALS = [
  {
    quote:
      "CRS delivered the rooftop programme across multiple sites with disciplined RAMS, weekly programme reporting and a snag closure rate we could actually evidence in board reports.",
    author: "Asset Manager, Housing Association",
  },
  {
    quote:
      "Mobilised quickly under our framework, coordinated with our M&E package and handed over commissioning records without us chasing.",
    author: "Project Manager, Main Contractor",
  },
  {
    quote:
      "Social-value reporting was tailored to our framework, not a generic PDF. That made the difference at panel.",
    author: "Procurement, Local Authority",
  },
];

export function LpCommercialSolarPv() {
  return (
    <>
      <PageBreadcrumb
        items={commercialBreadcrumbTrail("Commercial Solar PV", "/commercial-solar-pv")}
      />

      <section className="relative overflow-hidden">
        <HeroBackgroundPicture
          image={getRasterImage("commercial-solar-pv/hero-commercial-solar")}
          alt="Aerial view of a commercial rooftop solar PV array on a UK industrial building"
        />

        <div className="relative container-page py-8 md:py-12 lg:py-16">
          <div className="grid lg:grid-cols-5 gap-8 md:gap-10 items-start">
            <div className="lg:col-span-3 text-primary-foreground">
              <div className="mb-5 md:mb-6">
                <img
                  src={crsLogo}
                  alt="CRS Energy"
                  width={953}
                  height={408}
                  className="h-20 md:h-28 lg:h-32 w-auto"
                  loading="eager"
                  decoding="async"
                />
              </div>

              <p className="text-xs font-bold uppercase tracking-[0.2em] text-accent mb-3">
                For Councils, Housing Associations, FMs &amp; Main Contractors
              </p>
              <h1 className="text-3xl md:text-5xl lg:text-6xl font-bold text-balance leading-[1.05]">
                Commercial Solar PV In South Wales &amp; The South West Of England
              </h1>
              <p className="mt-4 md:mt-5 text-base md:text-lg lg:text-xl text-primary-foreground max-w-2xl text-pretty">
                MCS-certified commercial solar design and delivery, rooftop and ground-mount,{" "}
                <strong className="font-semibold">framework-ready</strong>, with documented{" "}
                <strong className="font-semibold">quality, carbon and social-value reporting</strong>.
              </p>

              <div className="mt-6 md:mt-7 flex flex-col sm:flex-row sm:flex-wrap gap-3">
                <CommercialPrimaryCta />
                <CommercialSecondaryCta ghost />
              </div>

              <ul className="hidden lg:flex flex-col gap-2 mt-7 rounded-2xl border border-white/20 bg-primary-foreground/5 backdrop-blur-sm p-4 max-w-md">
                {HERO_TRUST.map((item) => (
                  <li
                    key={item}
                    className="flex items-center gap-3 text-base font-semibold text-primary-foreground"
                  >
                    <ShieldCheck className="h-5 w-5 text-accent shrink-0" aria-hidden />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
              <ul className="lg:hidden mt-5 flex flex-wrap gap-2">
                {HERO_TRUST.map((item) => (
                  <li
                    key={item}
                    className="inline-flex items-center gap-2 rounded-full border border-white/25 bg-white/10 backdrop-blur-sm px-3 py-1.5 text-xs font-semibold text-primary-foreground"
                  >
                    <CheckCircle2 className="h-3.5 w-3.5 text-accent" aria-hidden />
                    {item}
                  </li>
                ))}
              </ul>
            </div>

            <div className="lg:col-span-2 p-4 sm:p-5 md:p-6 rounded-2xl bg-primary-foreground/10 backdrop-blur-xl border border-white/20 shadow-xl">
              <p className="text-xs uppercase font-bold tracking-wider mb-1 text-accent">B2B Enquiry</p>
              <h2 className="text-lg sm:text-xl font-bold mb-1 text-primary-foreground">
                Request A Feasibility Assessment
              </h2>
              <p className="text-sm text-primary-foreground/90 mb-4">
                For commercial, social-housing and framework enquiries. We&apos;ll respond from the commercial team.
              </p>
              <div className="[&_label]:text-white [&_input]:bg-white/15 [&_input]:text-white [&_input]:border-white/50 [&_input::placeholder]:text-white/75 [&_select]:bg-white/15 [&_select]:text-white [&_select]:border-white/50 [&_select]:[color-scheme:dark] [&_select_option]:bg-white [&_select_option]:text-foreground [&_textarea]:bg-white/15 [&_textarea]:text-white [&_textarea]:border-white/50">
                <B2BEnquiryForm
                  idPrefix="hero"
                  sourcePage={SOURCE_PAGE}
                  serviceOfInterest="Commercial Solar PV"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="border-b border-border/55 bg-secondary/25 py-8">
        <div className="container-page">
          <p className="text-center text-xs font-semibold tracking-[0.2em] uppercase text-muted-foreground mb-5">
            MCS Certified, Framework-Ready, Insurance-Backed
          </p>
          <AccreditationStrip />
          <p className="mt-4 text-center text-xs text-muted-foreground">
            MCS Certified Installer - Certificate No. IAA-10295 · HIES Consumer Code Member - Membership No. CRS/A/1864 · TrustMark Registered Business - Registration No. 2925406
          </p>
        </div>
      </section>

      <section className="border-b border-border/40 bg-background py-12 md:py-16">
        <div className="container-page">
          <div className="max-w-3xl mb-8 md:mb-10">
            <p className="text-xs font-bold uppercase tracking-widest text-accent mb-2">Who We Work With</p>
            <h2 className="text-2xl md:text-4xl font-bold text-foreground leading-tight">
              Sectors We Deliver Into
            </h2>
            <p className="text-sm md:text-base text-muted-foreground mt-3">
              Commercial solar delivered for buyers who need framework-grade quality, RAMS and reporting, not a
              homeowner-style brochure.
            </p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {SECTOR_CARDS.map((c) => {
              const inner = (
                <>
                  <div className="w-11 h-11 rounded-lg bg-accent/15 flex items-center justify-center mb-4">
                    <c.Icon className="h-5 w-5 text-accent" aria-hidden />
                  </div>
                  <h3 className="font-bold text-foreground text-base md:text-lg mb-2">{c.title}</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">{c.body}</p>
                  {c.href && (
                    <p className="mt-3 text-sm font-semibold text-accent inline-flex items-center gap-1">
                      View sector page <ArrowRight className="h-3.5 w-3.5" aria-hidden />
                    </p>
                  )}
                </>
              );
              return c.href ? (
                <a
                  key={c.title}
                  href={c.href}
                  className="rounded-2xl border border-border bg-card p-6 shadow-sm hover:border-accent/40 transition-colors block"
                >
                  {inner}
                </a>
              ) : (
                <article key={c.title} className="rounded-2xl border border-border bg-card p-6 shadow-sm">
                  {inner}
                </article>
              );
            })}
          </div>
        </div>
      </section>

      <section className="border-b border-border/40 bg-secondary/25 py-12 md:py-16">
        <div className="container-page">
          <div className="max-w-3xl mb-8 md:mb-10">
            <p className="text-xs font-bold uppercase tracking-widest text-accent mb-2">Why It Matters</p>
            <h2 className="text-2xl md:text-4xl font-bold text-foreground leading-tight">
              Cost Control, Decarbonisation, Reportable Social Value
            </h2>
            <p className="text-sm md:text-base text-muted-foreground mt-3">
              Procurement decisions need evidence, not slogans. Every claim is evidenced per project and flexible to
              your reporting framework.
            </p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {WHY_CARDS.map((c) => (
              <article key={c.title} className="rounded-2xl border border-border bg-card p-6 shadow-sm">
                <div className="w-11 h-11 rounded-lg bg-accent/15 flex items-center justify-center mb-4">
                  <c.Icon className="h-5 w-5 text-accent" aria-hidden />
                </div>
                <h3 className="font-bold text-foreground text-base md:text-lg mb-2">{c.title}</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">{c.body}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="border-b border-border/40 bg-background py-12 md:py-16">
        <div className="container-page">
          <div className="max-w-3xl mb-8 md:mb-10">
            <p className="text-xs font-bold uppercase tracking-widest text-accent mb-2">Scope</p>
            <h2 className="text-2xl md:text-4xl font-bold text-foreground leading-tight">What We Deliver</h2>
            <p className="text-sm md:text-base text-muted-foreground mt-3">
              From single rooftop arrays to portfolio rollouts, ground-mount and battery, delivered into new-build or
              occupied retrofit environments.
            </p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
            {SCOPE_CARDS.map((c) => (
              <article key={c.title} className="rounded-2xl border border-border bg-card p-5 shadow-sm">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-lg bg-accent/15 flex items-center justify-center shrink-0">
                    <c.Icon className="h-5 w-5 text-accent" aria-hidden />
                  </div>
                  <div>
                    <h3 className="font-bold text-foreground text-base mb-1">{c.title}</h3>
                    <p className="text-sm text-muted-foreground leading-relaxed">{c.body}</p>
                  </div>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="border-b border-border/40 bg-secondary/25 py-12 md:py-16">
        <div className="container-page">
          <div className="max-w-3xl mb-8 md:mb-10">
            <p className="text-xs font-bold uppercase tracking-widest text-accent mb-2">
              Mobilisation &amp; Delivery
            </p>
            <h2 className="text-2xl md:text-4xl font-bold text-foreground leading-tight">
              Coordination Is The Differentiator
            </h2>
            <p className="text-sm md:text-base text-muted-foreground mt-3">
              Mobilisation, design, DNO/G99, programme, RAMS, site management and subcontract coordination, all run
              as one programme under framework agreements.
            </p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {MOBILISATION.map((c) => (
              <article key={c.title} className="rounded-2xl border border-border bg-card p-5 shadow-sm">
                <div className="w-10 h-10 rounded-lg bg-accent/15 flex items-center justify-center mb-3">
                  <c.Icon className="h-5 w-5 text-accent" aria-hidden />
                </div>
                <h3 className="font-bold text-foreground text-sm md:text-base mb-1.5">{c.title}</h3>
                <p className="text-xs md:text-sm text-muted-foreground leading-relaxed">{c.body}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="border-b border-border/40 bg-background py-12 md:py-16">
        <div className="container-page">
          <div className="max-w-3xl mb-8 md:mb-10">
            <p className="text-xs font-bold uppercase tracking-widest text-accent mb-2">KPIs &amp; Reporting</p>
            <h2 className="text-2xl md:text-4xl font-bold text-foreground leading-tight">
              Visible Performance, Documented Accountability
            </h2>
            <p className="text-sm md:text-base text-muted-foreground mt-3">
              Buyers see what&apos;s happening on programme, on H&amp;S, on quality, on generation and on social
              value, in formats their boards and panels expect.
            </p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
            {KPI_CARDS.map((c) => (
              <article key={c.title} className="rounded-2xl border border-border bg-card p-5 shadow-sm">
                <div className="w-10 h-10 rounded-lg bg-accent/15 flex items-center justify-center mb-3">
                  <c.Icon className="h-5 w-5 text-accent" aria-hidden />
                </div>
                <h3 className="font-bold text-foreground text-base mb-1.5">{c.title}</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">{c.body}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <CommercialAccreditationsTable
        rows={SOLAR_COMPLIANCE_ROWS}
        showViewAllLink
        viewAllHref={COMMERCIAL_URLS.compliance}
      />
      <CommercialSocialValue showViewAllLink viewAllHref={COMMERCIAL_URLS.socialValue} />

      <CaseStudiesSection
        variant="embedded"
        showViewAllLink
        viewAllHref={COMMERCIAL_URLS.caseStudies}
        gridSize="compact"
      />

      <section className="border-b border-border/40 bg-secondary/35 py-12 md:py-16">
        <div className="container-page max-w-5xl">
          <div className="rounded-2xl border-2 border-dashed border-accent/60 bg-card p-6 md:p-8 shadow-sm">
            <div className="flex items-center gap-2 mb-3">
              <PoundSterling className="h-5 w-5 text-accent" aria-hidden />
              <p className="text-xs font-bold uppercase tracking-widest text-accent">Finance &amp; PPA (B2B)</p>
            </div>
            <h2 className="text-2xl md:text-3xl font-bold text-foreground leading-tight mb-4">
              Purchase, Finance Or PPA
            </h2>
            <p className="text-sm md:text-base text-muted-foreground leading-relaxed mb-4">
              Commercial solar can be delivered as a direct purchase, on finance, or through a Power Purchase
              Agreement (PPA), depending on the buyer&apos;s preferred capital structure.
            </p>

            <div className="rounded-xl border border-amber-300/70 bg-amber-50 dark:bg-amber-950/30 p-4 mb-5">
              <p className="text-xs font-bold uppercase tracking-wider text-amber-900 dark:text-amber-200 mb-2">
                Unpublished, Pending Compliance Sign-Off
              </p>
              <p className="text-sm text-amber-900/90 dark:text-amber-100/90 leading-relaxed">
                All APR, representative example, PPA pricing, term sheets, eligibility, FCA wording and Appointed
                Representative status to be confirmed before this section is published. ROI is evidenced per project,
                never quoted generically.
              </p>
            </div>

            <div className="grid sm:grid-cols-3 gap-4">
              {[
                {
                  title: "Purchase",
                  body: "Direct capital purchase, written quote, project-level ROI evidenced.",
                },
                { title: "Finance", body: "Spread the capital cost over an agreed term." },
                { title: "PPA", body: "No upfront capital; pay per kWh generated." },
              ].map((opt) => (
                <article key={opt.title} className="rounded-xl border border-border bg-background p-4">
                  <h3 className="font-bold text-foreground mb-1.5">{opt.title}</h3>
                  <p className="text-xs md:text-sm text-muted-foreground leading-relaxed">{opt.body}</p>
                </article>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="border-b border-border bg-card py-10 md:py-16">
        <div className="container-page">
          <h2 className="text-2xl md:text-3xl font-bold text-foreground mb-6 md:mb-8 text-center">Our Process</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
            {PROCESS_STEPS.map((s) => (
              <div key={s.step} className="rounded-2xl border border-border bg-background p-5 shadow-sm">
                <div className="w-10 h-10 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-base font-extrabold mb-4">
                  {s.step}
                </div>
                <h3 className="font-bold text-foreground mb-2">{s.title}</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">{s.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="border-b border-border/40 bg-background py-12 md:py-16">
        <div className="container-page">
          <div className="max-w-3xl mb-8 md:mb-10">
            <div className="flex items-center gap-2 mb-3">
              <ShieldCheck className="h-5 w-5 text-accent" aria-hidden />
              <p className="text-xs font-bold uppercase tracking-widest text-accent">Trust &amp; Track Record</p>
            </div>
            <h2 className="text-2xl md:text-4xl font-bold text-foreground leading-tight">
              What Procurement, Asset Managers &amp; Main Contractors Say
            </h2>
          </div>
          <div className="grid lg:grid-cols-3 gap-5">
            {B2B_TESTIMONIALS.map((t) => (
              <figure key={t.author} className="rounded-2xl border border-border bg-card p-6 shadow-sm">
                <blockquote className="text-sm md:text-base text-foreground leading-relaxed">
                  &ldquo;{t.quote}&rdquo;
                </blockquote>
                <figcaption className="mt-4 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                  {t.author}
                </figcaption>
              </figure>
            ))}
          </div>
        </div>
      </section>

      <section className="border-b border-border/40 bg-secondary/25 py-12 md:py-16">
        <div className="container-page max-w-4xl">
          <div className="mb-8">
            <p className="text-xs font-bold uppercase tracking-widest text-accent mb-2">FAQ</p>
            <h2 className="text-2xl md:text-4xl font-bold text-foreground leading-tight">
              Procurement &amp; Delivery Questions
            </h2>
          </div>
          <dl className="space-y-4">
            {COMMERCIAL_SOLAR_FAQS.map((f) => (
              <div key={f.question} className="rounded-2xl border border-border bg-card p-5 md:p-6 shadow-sm">
                <dt className="font-bold text-foreground text-base md:text-lg mb-2 flex items-start gap-2">
                  <Wrench className="h-4 w-4 text-accent mt-1 shrink-0" aria-hidden />
                  {f.question}
                </dt>
                <dd className="text-sm md:text-base text-muted-foreground leading-relaxed">{f.answer}</dd>
              </div>
            ))}
          </dl>
        </div>
      </section>

      <span id="contact" aria-hidden className="block scroll-mt-32" />
      <section
        id="bottom-cta"
        className="scroll-mt-32 border-t border-border/50 bg-gradient-to-b from-muted/30 to-secondary/35 py-12 md:py-20"
      >
        <div className="container-page max-w-3xl">
          <div className="text-center mb-8">
            <h2 className="text-2xl md:text-3xl font-bold text-foreground mb-2">
              Request A Feasibility Assessment
            </h2>
            <p className="text-sm md:text-base text-muted-foreground">
              For commercial, social-housing and framework enquiries. Or call{" "}
              <a href={PHONE_TEL} className="text-accent font-semibold">
                {PHONE_LABEL}
              </a>
              .
            </p>
          </div>
          <div className="rounded-2xl border border-border bg-card p-5 sm:p-6 md:p-8 shadow-soft">
            <B2BEnquiryForm
              idPrefix="bottom"
              sourcePage={SOURCE_PAGE}
              serviceOfInterest="Commercial Solar PV"
            />
          </div>
          <p className="text-xs text-muted-foreground text-center mt-5">
            Capability Statement and Accreditation Pack available on request.
          </p>
        </div>
      </section>
    </>
  );
}
